import { DocumentLoadEvent } from "@react-pdf-viewer/core";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { FeatureFlag } from "../../constants/featureFlags";
import {
  MemoryMonitorService,
  measureCanvasMemoryMb,
} from "../../services/posthog/MemoryMonitorService";
import { useFeatureFlag } from "../useFeatureFlag";

export function usePdfMemoryTracking(fileUrl: string): {
  onDocumentLoad: (e: DocumentLoadEvent) => void;
} {
  const isMemoryMonitorEnabled = useFeatureFlag(FeatureFlag.MemoryMonitor);

  const heapBeforeRef = useRef<number | null>(null);
  const heapPctBeforeRef = useRef<number | null>(null);
  const openTimeRef = useRef(Date.now());
  const closeTimeRef = useRef(0);
  const docWeakRef = useRef<WeakRef<object> | null>(null);
  const gcCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enabledRef = useRef(isMemoryMonitorEnabled);
  enabledRef.current = isMemoryMonitorEnabled;

  const registry = useMemo(
    () =>
      "FinalizationRegistry" in window
        ? new FinalizationRegistry<null>(() => {
            if (!enabledRef.current) return;
            posthog.capture("pdf_document_gc", {
              gc_latency_s:
                closeTimeRef.current > 0 ? (Date.now() - closeTimeRef.current) / 1000 : null,
            });
          })
        : null,
    []
  );

  useEffect(() => {
    docWeakRef.current = null;
    // Known gap: if isMemoryMonitorEnabled is still resolving (PostHog flags haven't
    // loaded) and this fileUrl's session ends before it resolves, that session's
    // open/close is not tracked. Accepted: diagnostic telemetry, sub-2s window,
    // low-value edge for a crash-diagnosis tool.
    if (!isMemoryMonitorEnabled) return;
    const service = MemoryMonitorService.getInstance();
    const snap = service.takeSnapshot();
    heapBeforeRef.current = snap?.heapUsedMb ?? null;
    heapPctBeforeRef.current = snap?.heapUsedPct ?? null;
    openTimeRef.current = Date.now();

    posthog.capture("pdf_viewer_opened", {
      heap_before_mb: heapBeforeRef.current,
      heap_used_pct_before: heapPctBeforeRef.current,
    });
    service.trackPdfOpen();

    return () => {
      closeTimeRef.current = Date.now();
      const snapAfter = service.takeSnapshot();
      const heapAfterMb = snapAfter?.heapUsedMb ?? null;
      const heapDeltaMb =
        heapBeforeRef.current !== null && heapAfterMb !== null
          ? heapAfterMb - heapBeforeRef.current
          : null;
      const viewDurationS = (closeTimeRef.current - openTimeRef.current) / 1000;
      const canvasMemoryAfterMb = measureCanvasMemoryMb();

      const pdfEntry = (
        performance.getEntriesByType("resource") as PerformanceResourceTiming[]
      ).find((e) => e.name === fileUrl);
      const pdfTransferSizeMb = pdfEntry ? pdfEntry.transferSize / (1024 * 1024) : null;

      posthog.capture("pdf_viewer_closed", {
        heap_before_mb: heapBeforeRef.current,
        heap_after_mb: heapAfterMb,
        heap_delta_mb: heapDeltaMb,
        canvas_memory_after_mb: canvasMemoryAfterMb,
        pdf_transfer_size_mb: pdfTransferSizeMb,
        view_duration_s: viewDurationS,
      });

      service.trackPdfClose();

      if (heapDeltaMb !== null && heapDeltaMb > 50) {
        posthog.capture("pdf_memory_leak_suspected", {
          heap_delta_mb: heapDeltaMb,
          heap_used_pct_after: snapAfter?.heapUsedPct ?? null,
          view_duration_s: viewDurationS,
        });
      }

      if (gcCheckTimerRef.current !== null) {
        clearTimeout(gcCheckTimerRef.current);
        gcCheckTimerRef.current = null;
      }
      if ("WeakRef" in window && docWeakRef.current !== null) {
        const weakRef = docWeakRef.current;
        gcCheckTimerRef.current = setTimeout(() => {
          gcCheckTimerRef.current = null;
          if (weakRef.deref() !== undefined) {
            posthog.capture("pdf_document_not_gc_suspected", {
              check_after_s: 60,
              heap_used_pct: MemoryMonitorService.getInstance().takeSnapshot()?.heapUsedPct ?? null,
            });
          }
        }, 60_000);
      }
    };
  }, [fileUrl, isMemoryMonitorEnabled]);

  const onDocumentLoad = useCallback(
    (e: DocumentLoadEvent) => {
      if (!isMemoryMonitorEnabled) return;
      if ("WeakRef" in window) {
        docWeakRef.current = new WeakRef(e.doc as object);
      }
      if (registry) {
        registry.register(e.doc as object, null);
      }
    },
    [registry, isMemoryMonitorEnabled]
  );

  return { onDocumentLoad };
}
